// Full Stock Take Review Page - Review and Finalise
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Save,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Package,
  AlertCircle,
  FileText,
  Download,
  ClipboardList,
  RefreshCw,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { format } from "date-fns";

interface CountItem {
  code: string;
  description: string;
  category: string;
  location: string;
  systemQty: number;
  countedQty: string;
  variance: number;
  notes: string;
  isCounted: boolean;
  warehouseCode: string;
}

interface StockTakesFullReviewPageProps {
  setupData?: {
    description: string;
    date: Date;
    warehouse: string;
    notes: string;
    assignedTo: string;
    type: string;
  };
  countedItems?: CountItem[];
}

export function StockTakesFullReviewPage({ setupData, countedItems = [] }: StockTakesFullReviewPageProps) {
  const { navigateTo } = useNavigation();
  const [finalNotes, setFinalNotes] = useState("");
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [finalizeResult, setFinalizeResult] = useState<{
    reference: string;
    itemsUpdated: number;
    adjustmentsCreated: number;
    totalVarianceValue: number;
  } | null>(null);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = countedItems.length;
    const counted = countedItems.filter(item => item.isCounted).length;
    const notCounted = total - counted;
    const variances = countedItems.filter(item => item.variance !== 0).length;
    const increases = countedItems.filter(item => item.variance > 0).length;
    const decreases = countedItems.filter(item => item.variance < 0).length;
    const totalVarianceQty = countedItems.reduce((sum, item) => sum + Math.abs(item.variance), 0);
    
    // Calculate total variance value (assuming average price of $10 per unit for demo)
    const totalVarianceValue = countedItems.reduce((sum, item) => {
      const avgPrice = 10; // Mock average price
      return sum + (item.variance * avgPrice);
    }, 0);

    return {
      total,
      counted,
      notCounted,
      variances,
      increases,
      decreases,
      totalVarianceQty,
      totalVarianceValue,
    };
  }, [countedItems]);

  // Get items with variances, sorted by absolute variance value
  const varianceItems = useMemo(() => {
    return countedItems
      .filter(item => item.variance !== 0)
      .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance))
      .slice(0, 20); // Show top 20 variances
  }, [countedItems]);

  const handleSaveDraft = () => {
    const draftData = {
      setupData,
      countedItems,
      finalNotes,
      savedAt: new Date().toISOString(),
      status: "draft",
    };
    console.log("Saving draft:", draftData);
    // TODO: Save to storage
    alert("Draft saved successfully!");
  };

  const handleFinalize = () => {
    // Show confirmation dialog
    setShowFinalizeDialog(true);
  };

  const confirmFinalize = () => {
    const reference = `FST-${format(new Date(), "yyyyMMdd")}-${Math.floor(Math.random() * 1000).toString().padStart(3, "0")}`;
    
    // 1. Create stock take record
    const stockTakeData = {
      id: `ST-${Date.now()}`,
      reference,
      type: "Full Stock Take",
      description: setupData?.description || "",
      date: setupData?.date || new Date(),
      warehouse: setupData?.warehouse || "All Warehouses",
      assignedTo: setupData?.assignedTo || "Unassigned",
      notes: setupData?.notes || "",
      finalNotes,
      items: countedItems,
      statistics: stats,
      status: "completed",
      completedAt: new Date().toISOString(),
      completedBy: setupData?.assignedTo || "System",
      createdAt: new Date().toISOString(),
    };

    // 2. Create stock adjustments for variances
    const adjustments = countedItems
      .filter(item => item.variance !== 0 && item.isCounted)
      .map(item => ({
        id: `ADJ-${Date.now()}-${item.code}`,
        reference: `${reference}-${item.code}`,
        stockItemCode: item.code,
        description: item.description,
        type: item.variance > 0 ? "increase" : "decrease",
        quantity: Math.abs(item.variance),
        reason: "Stock Take Adjustment",
        stockTakeReference: reference,
        notes: item.notes || "Adjusted from stock take",
        date: new Date().toISOString(),
        createdBy: setupData?.assignedTo || "System",
      }));

    // 3. Update stock levels (in real app, this would update the database)
    const updatedStockItems = countedItems
      .filter(item => item.isCounted)
      .map(item => ({
        code: item.code,
        previousQty: item.systemQty,
        newQty: parseFloat(item.countedQty) || 0,
        variance: item.variance,
      }));

    console.log("Finalising stock take:", {
      stockTake: stockTakeData,
      adjustments,
      stockUpdates: updatedStockItems,
    });
    
    // Save stock take to localStorage
    const existingStockTakes = JSON.parse(localStorage.getItem("stockTakes") || "[]");
    existingStockTakes.push(stockTakeData);
    localStorage.setItem("stockTakes", JSON.stringify(existingStockTakes));

    // Save adjustments to localStorage
    const existingAdjustments = JSON.parse(localStorage.getItem("stockAdjustments") || "[]");
    existingAdjustments.push(...adjustments);
    localStorage.setItem("stockAdjustments", JSON.stringify(existingAdjustments));

    // In a real application, you would also:
    // - Update stock item quantities in the database
    // - Create audit trail entries
    // - Send notifications to relevant users
    // - Generate and store the final report

    setShowFinalizeDialog(false);
    setShowSuccessDialog(true);
    setFinalizeResult({
      reference,
      itemsUpdated: updatedStockItems.length,
      adjustmentsCreated: adjustments.length,
      totalVarianceValue: stats.totalVarianceValue,
    });
  };

  const handleExportReport = () => {
    console.log("Exporting report...");
    alert("Report export functionality coming soon!");
  };

  const getVarianceBadge = (variance: number) => {
    if (variance > 0) {
      return (
        <Badge variant="outline" className="gap-1 border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
          <TrendingUp className="h-3 w-3" />
          +{variance}
        </Badge>
      );
    }
    if (variance < 0) {
      return (
        <Badge variant="outline" className="gap-1 border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <TrendingDown className="h-3 w-3" />
          {variance}
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Fixed Header */}
      <div className="flex-shrink-0 pb-4 border-b bg-background">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateTo("stock-takes/full/count", { setupData, countedItems })}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Count
          </Button>
        </div>

        {/* Title Section */}
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
            <ClipboardList className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Full Stock Take</h1>
              <Badge variant="secondary">Step 3 of 3</Badge>
            </div>
            <p className="text-muted-foreground">Review and finalise your stock take</p>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto py-6 space-y-6">
        {/* Statistics Overview */}
        <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Items</p>
                <p className="text-3xl font-bold">{stats.total}</p>
              </div>
              <Package className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Counted</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.counted}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.notCounted} not counted
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Variances</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">{stats.variances}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.increases} increases, {stats.decreases} decreases
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Variance</p>
                <p className="text-3xl font-bold">{stats.totalVarianceQty}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  ${Math.abs(stats.totalVarianceValue).toFixed(2)} value
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stock Take Details */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Stock Take Details</CardTitle>
          <CardDescription>Summary of the stock take configuration</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Description</p>
                  <p className="font-medium">{setupData?.description || "No description"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Count Date</p>
                  <p className="font-medium">
                    {setupData?.date ? format(new Date(setupData.date), "MMMM do, yyyy") : "Not set"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Warehouse</p>
                  <p className="font-medium">
                    {setupData?.warehouse === "all" ? "All Warehouses" : setupData?.warehouse || "Not specified"}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Assigned To</p>
                  <p className="font-medium">{setupData?.assignedTo || "Unassigned"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Setup Notes</p>
                  <p className="font-medium">{setupData?.notes || "No notes"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Completion Rate</p>
                  <p className="font-medium">
                    {stats.total > 0 ? ((stats.counted / stats.total) * 100).toFixed(1) : 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Variance Analysis */}
      {varianceItems.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Variance Analysis</CardTitle>
            <CardDescription>
              Top {varianceItems.length} items with variances (sorted by quantity)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-auto max-h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Code</TableHead>
                    <TableHead className="min-w-[200px]">Description</TableHead>
                    <TableHead className="w-[120px]">Category</TableHead>
                    <TableHead className="w-[100px] text-right">System Qty</TableHead>
                    <TableHead className="w-[100px] text-right">Counted Qty</TableHead>
                    <TableHead className="w-[120px]">Variance</TableHead>
                    <TableHead className="min-w-[200px]">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {varianceItems.map((item) => (
                    <TableRow key={item.code}>
                      <TableCell className="font-medium">{item.code}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.category}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{item.systemQty}</TableCell>
                      <TableCell className="text-right font-medium">
                        {item.countedQty || "0"}
                      </TableCell>
                      <TableCell>{getVarianceBadge(item.variance)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {item.notes || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Final Notes */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Final Notes</CardTitle>
          <CardDescription>Add any final comments or observations about this stock take</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={finalNotes}
            onChange={(e) => setFinalNotes(e.target.value)}
            placeholder="Enter any final notes, observations, or actions required..."
            className="min-h-[120px]"
            rows={5}
          />
        </CardContent>
      </Card>

      </div>

      {/* Fixed Action Bar */}
      <div className="flex-shrink-0 border-t bg-background shadow-lg p-4">
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigateTo("stock-takes/full/count", { setupData, countedItems })}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Count
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExportReport} className="gap-2">
              <Download className="h-4 w-4" />
              Export Report
            </Button>
            <Button variant="outline" onClick={handleSaveDraft} className="gap-2">
              <Save className="h-4 w-4" />
              Save Draft
            </Button>
            <Button onClick={handleFinalize} className="gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Finalise Stock Take
            </Button>
          </div>
        </div>
      </div>

      {/* Finalise Confirmation Dialog */}
      <Dialog open={showFinalizeDialog} onOpenChange={setShowFinalizeDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Finalise Stock Take?</DialogTitle>
            <DialogDescription>
              This will complete the stock take and make the following changes to your inventory:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                      <RefreshCw className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Stock Items Updated</p>
                      <p className="text-2xl font-bold">{stats.counted}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                      <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Adjustments Created</p>
                      <p className="text-2xl font-bold">{stats.variances}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="rounded-lg border p-4 bg-muted/50">
              <h4 className="font-semibold mb-3">What will happen:</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                  <span>Stock quantities will be updated to match counted values</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                  <span>Stock adjustment records will be created for all variances</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                  <span>Stock take will be marked as completed and locked</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                  <span>Audit trail will be created for compliance</span>
                </li>
              </ul>
            </div>
            {stats.variances > 0 && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20 p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5" />
                  <div>
                    <p className="font-semibold text-orange-900 dark:text-orange-100">
                      {stats.variances} Variance{stats.variances !== 1 ? "s" : ""} Detected
                    </p>
                    <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                      Total variance value: ${Math.abs(stats.totalVarianceValue).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinalizeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmFinalize}>
              Confirm & Finalise
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 rounded-full bg-green-50 dark:bg-green-900/20">
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              Stock Take Completed!
            </DialogTitle>
            <DialogDescription>
              Your stock take has been successfully finalised and all changes have been applied.
            </DialogDescription>
          </DialogHeader>
          {finalizeResult && (
            <div className="space-y-4 py-4">
              <div className="rounded-lg border p-4 bg-muted/50">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Reference Number</span>
                    <span className="font-mono font-semibold">{finalizeResult.reference}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Items Updated</span>
                    <span className="font-semibold">{finalizeResult.itemsUpdated}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Adjustments Created</span>
                    <span className="font-semibold">{finalizeResult.adjustmentsCreated}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Variance Value</span>
                    <span className="font-semibold">${Math.abs(finalizeResult.totalVarianceValue).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20 p-4">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-green-900 dark:text-green-100">
                      All changes have been applied
                    </p>
                    <p className="text-green-700 dark:text-green-300 mt-1">
                      Stock levels have been updated and adjustment records have been created for audit purposes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => {
              setShowSuccessDialog(false);
              navigateTo("stock-takes");
            }}>
              Back to Stock Takes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
